import React, { memo } from 'react'

import {
  BrushAI, Button, CubeAIOutline, Download, Icon, Refresh,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { NOOP } from 'Utils/stableDefaults'

import LikeButton from './Components/LikeButton/LikeButton'

const regenerateIcon = <Icon svgComponent={Refresh} size={iconSize} notSpaced />
const editIcon = <Icon svgComponent={BrushAI} size={iconSize} notSpaced />
const downloadIcon = <Icon svgComponent={Download} size={iconSize} notSpaced />
const generateIcon = <Icon svgComponent={CubeAIOutline} size={iconSize} notSpaced />

// Like / Regenerate / Edit / Download / Generate 3D under the expanded image. Regenerate only
// shows where the host passes `onRegenerate` (the /ai-image/:slug page, not the gallery).
function Actions({
  title,
  slug = '',
  likeCount = 0,
  isRegenerating = false,
  onRegenerate = null,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
}) {
  const t = useTranslation()
  const hasPremiumFeatures = useThreeDStudioStore(state => state.subscription.premiumFeatures) === true

  return (
    <div className="image-gallery-modal__actions">
      {/* Keyed on the image: the modal stays mounted between images, and each one's like
          state must start from its own count rather than the previous image's. */}
      {slug && <LikeButton key={slug} slug={slug} title={title} initialCount={likeCount} />}
      {onRegenerate && (
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          icon={regenerateIcon}
          customClass="image-gallery-modal__action"
          disabled={isRegenerating}
          onClick={onRegenerate}
          aria-label={t('imageGallery.card.regenerate', { title })}
        >
          {t('imageGallery.modal.regenerate')}
        </Button>
      )}
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        actionType="button"
        icon={editIcon}
        customClass="image-gallery-modal__action"
        onClick={onEdit}
        aria-label={t('imageGallery.card.edit', { title })}
      >
        {t('imageGallery.modal.edit')}
      </Button>
      <span className="image-gallery-modal__action-wrap">
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          icon={downloadIcon}
          customClass="image-gallery-modal__action image-gallery-modal__action--download"
          onClick={onDownload}
          aria-label={t('imageGallery.card.download', { title })}
        >
          {t('imageGallery.modal.download')}
        </Button>
        {!hasPremiumFeatures && (
          <PaidFeatureBadge
            className="image-gallery-modal__action-badge"
            label={t('shared.subscribersOnly.title')}
          />
        )}
      </span>
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        actionType="button"
        icon={generateIcon}
        customClass="image-gallery-modal__action image-gallery-modal__action--ai"
        onClick={onGenerate3d}
        aria-label={t('imageGallery.card.generate3dFor', { title })}
      >
        {t('imageGallery.card.generate3d')}
      </Button>
    </div>
  )
}

Actions.propTypes = {
  title: string.isRequired,
  slug: string,
  likeCount: number,
  isRegenerating: bool,
  onRegenerate: func,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
}

export default memo(Actions)
